import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, CreditCard, XCircle, Clock } from "lucide-react";
import { SetupFeeNote } from "@/components/ShopPricingInfo";
import { toast } from "sonner";

export default function ShopSetup() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const shopId = params.get("shop");
  const sessionId = params.get("session_id");
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(sessionId ? "verifying" : "idle");

  useEffect(() => {
    if (!shopId) return;
    api.get(`/public/shops/${shopId}/setup-info`).then(({ data }) => setInfo(data)).catch(() => setInfo(false));
  }, [shopId]);

  useEffect(() => {
    if (!sessionId) return;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/public/setup/status/${sessionId}`);
        if (data.payment_status === "paid") return setStatus("activated");
      } catch (e) {}
      if (tries++ < 20) setTimeout(poll, 2000);
      else setStatus("error");
    };
    poll();
  }, [sessionId]);

  const pay = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/public/shops/${shopId}/setup-checkout`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setLoading(false);
    }
  };

  const Wrapper = ({ children }) => (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="flex justify-center mb-8">
          <div className="bg-white rounded-xl px-4 py-3 inline-flex"><img src="/velopaths-logo.png" alt="Velopaths" className="h-9 w-auto" /></div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-8">{children}</div>
      </div>
    </div>
  );

  if (!shopId) {
    return <Wrapper><div className="text-center"><XCircle className="w-12 h-12 text-destructive mx-auto mb-4" /><h1 className="font-heading text-xl font-bold">Invalid setup link</h1></div></Wrapper>;
  }

  if (status === "verifying") {
    return <Wrapper><div className="text-center py-6" data-testid="setup-verifying"><Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" /><h1 className="font-heading text-2xl font-bold">Confirming your payment…</h1></div></Wrapper>;
  }

  if (status === "activated" || (info && info.active)) {
    return (
      <Wrapper>
        <div className="text-center py-4" data-testid="setup-activated">
          <CheckCircle2 className="w-14 h-14 text-primary mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-bold mb-2">You're now activated on Velopaths.</h1>
          <p className="text-muted-foreground text-sm mb-6">Welcome aboard! Your first zone is included — claim your complimentary Founding Partner territory now.</p>
          <Button size="lg" className="w-full" onClick={() => navigate("/claim-zone")} data-testid="setup-claim-zone">Claim your complimentary zone</Button>
          <Button variant="ghost" size="sm" className="w-full mt-2" onClick={() => navigate("/shop")} data-testid="setup-go-dashboard">Skip for now — go to dashboard</Button>
        </div>
      </Wrapper>
    );
  }

  if (status === "error") {
    return <Wrapper><div className="text-center py-4"><XCircle className="w-14 h-14 text-destructive mx-auto mb-4" /><h1 className="font-heading text-2xl font-bold mb-2">We couldn't confirm your payment</h1><p className="text-muted-foreground text-sm mb-6">No activation was applied. You can try the setup fee again.</p><Button className="w-full" onClick={() => navigate(`/pay/setup?shop=${shopId}`)}>Try again</Button></div></Wrapper>;
  }

  if (info === false) {
    return <Wrapper><div className="text-center"><XCircle className="w-12 h-12 text-destructive mx-auto mb-4" /><h1 className="font-heading text-xl font-bold">Partner not found</h1></div></Wrapper>;
  }

  if (info && !info.approved) {
    return <Wrapper><div className="text-center py-4"><Clock className="w-14 h-14 text-amber-400 mx-auto mb-4" /><h1 className="font-heading text-2xl font-bold mb-2">Awaiting approval</h1><p className="text-muted-foreground text-sm">This application hasn't been approved yet. You'll receive a payment link by email once Velopaths approves your application.</p></div></Wrapper>;
  }

  if (info === null) return <Wrapper><div className="text-center py-6"><Loader2 className="w-8 h-8 text-primary animate-spin mx-auto" /></div></Wrapper>;
  const feeStr = info?.fee != null ? `$${Number(info.fee) % 1 === 0 ? info.fee : Number(info.fee).toFixed(2)}` : "$—";
  return (
    <Wrapper>
      <div data-testid="setup-pay">
        <div className="flex items-center gap-2 mb-2"><CreditCard className="w-5 h-5 text-primary" /><h1 className="font-heading text-2xl font-bold">Complete your enrollment</h1></div>
        {info?.name && <p className="text-sm text-primary font-medium mb-4">{info.name}</p>}
        <p className="text-sm text-muted-foreground leading-relaxed mb-6">
          To complete your Velopaths Founding Partner enrollment, please pay the one-time {feeStr} setup fee. This <strong>includes your first zone and one year of participation</strong> — covering onboarding,
          your shop profile, and your curated advertiser placements. After payment, your shop will be
          activated and you'll gain access to your partner dashboard and revenue sharing tools.
        </p>
        <div className="flex items-baseline gap-2 mb-6">
          <span className="text-4xl font-heading font-extrabold">{feeStr}</span>
          <span className="text-muted-foreground text-sm">one-time setup fee</span>
        </div>
        <SetupFeeNote fee={info?.fee ?? 99} className="mb-6" />
        <Button size="lg" className="w-full" onClick={pay} disabled={loading} data-testid="pay-setup-button">
          {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}<CreditCard className="w-4 h-4 mr-2" />Pay {feeStr} & activate my account
        </Button>
        <p className="text-xs text-muted-foreground mt-3 text-center">Secure checkout via Stripe</p>
      </div>
    </Wrapper>
  );
}
